import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import Visibility from '@mui/icons-material/VisibilityOutlined'
import VisibilityOff from '@mui/icons-material/VisibilityOffOutlined'
import Box from '@mui/material/Box'
import { LoadingButton } from '@mui/lab'
import SVG from 'react-inlinesvg'

import { AuthFormBox } from './__styled'
import { Input } from '@/components'
import { validateEmail, validatePhoneNumber } from '@/utils/helper'
import { useLogin, useUpdateOnlineStatus, useLoginCognito } from '@/hooks/api/auth'
import { useAuth } from '@/hooks/common/useAuth'
import { toast } from 'react-hot-toast'
import { useTheme } from '@mui/material'
import { SVGS } from '@/assets/svgs'

export default function Login() {
  const [user, setUser] = useState('') // Email or Phone number
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [editing, setEditing] = useState(false)
  const theme = useTheme()

  const login = useLogin(user, password)
  const loginCognito = useLoginCognito(login.data || '')
  const updateOnlineStatus = useUpdateOnlineStatus(1)
  const { login: localLogin } = useAuth()

  useEffect(() => {
    if (login.isSuccess) {
      loginCognito.mutate()
    }
  }, [login.isSuccess, login.data])

  useEffect(() => {
    if (loginCognito.isSuccess) {
      if (loginCognito.data.accountStatus === '0') {
        toast.error('User is inactive. Activate user and retry.')
        return
      } else if (loginCognito.data.errorCat === 4) {
        toast.error('User is deleted. Create new one.')
        return
      }
      updateOnlineStatus.mutate()
      const { requestID, userID, userName } = loginCognito.data
      localLogin({ requestID, userID, userName })
    }
  }, [loginCognito.isSuccess, loginCognito.data])

  const formError = useMemo(() => {
    const error: Record<string, string> = {}
    if (!user.length) {
      error.user = 'Email or Phone Number is required'
    } else if (!validateEmail(user) && !validatePhoneNumber(user)) {
      error.user = 'Enter Valid Phone/Email'
    }
    return error
  }, [user, password])

  const onChangeUser = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUser(e.target.value)
    setEditing(true)
  }

  const onChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
  }

  const onSubmitLogin = () => {
    if (formError.user || !password) return
    login.mutate()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === 'Enter') {
      onSubmitLogin()
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        p: { xs: 3, sm: 4, md: 6 },
      }}
    >
      <Box sx={{ width: '100%', maxWidth: '440px' }}>
        {/* Header */}
        <Box mb={4}>
          <Typography
            variant='h4'
            fontWeight={700}
            color='#0f172a'
            mb={1.5}
            sx={{
              fontSize: { xs: '32px', md: '36px' },
              letterSpacing: '-0.02em',
            }}
          >
            Welcome Back
          </Typography>
          <Typography variant='body1' color='#64748b' lineHeight={1.6} sx={{ fontSize: '16px' }}>
            Continue creating stunning designs
          </Typography>
        </Box>

        {/* Form */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Input
            value={user}
            placeholder='Email or Phone Number'
            onChange={onChangeUser}
            errorText={formError.user}
            isError={editing && !!formError.user}
            name='email'
          />
          <Input
            value={password}
            placeholder='Password'
            onChange={onChangePassword}
            rightIcon={<SVG src={showPassword ? SVGS.VisibilityOffIcon : SVGS.VisibilityIcon} />}
            onToggleIcon={() => setShowPassword(!showPassword)}
            type={showPassword ? 'text' : 'password'}
            errorText={formError.password}
            isError={!!formError.password}
            name='password'
            onKeyDown={handleKeyDown}
          />

          {/* Error Message */}
          {!login.isLoading && !!login.error && (
            <Box
              sx={{
                p: 1.5,
                backgroundColor: '#fee',
                borderRadius: '8px',
                border: '1px solid #fcc',
              }}
            >
              <Typography variant='body2' color='error.main' fontSize='14px'>
                Incorrect username or password.
              </Typography>
            </Box>
          )}

          {/* Forgot Password Link */}
          <Box display='flex' justifyContent='flex-end'>
            <Link to='/forgot-password' style={{ textDecoration: 'none' }}>
              <Typography
                variant='body2'
                sx={{
                  color: '#6366f1',
                  fontWeight: 600,
                  fontSize: '14px',
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: '#5558e3',
                  },
                }}
              >
                Forgot password?
              </Typography>
            </Link>
          </Box>

          {/* Sign In Button */}
          <LoadingButton
            variant='contained'
            size='large'
            fullWidth
            disabled={!!formError.user || !password}
            onClick={onSubmitLogin}
            loading={loginCognito.isLoading || login.isLoading}
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '16px',
              letterSpacing: '-0.01em',
              py: '14px',
              mt: 1,
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5558e3 0%, #7c4ee8 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(99, 102, 241, 0.4)',
              },
              '&:active': {
                transform: 'translateY(0px)',
              },
              '&:disabled': {
                background: '#e2e8f0',
                color: '#94a3b8',
                boxShadow: 'none',
              },
            }}
          >
            Sign In
          </LoadingButton>

          {/* Sign Up Link */}
          <Box display='flex' justifyContent='center' alignItems='center' gap={0.5} mt={2}>
            <Typography variant='body2' color='#64748b' fontSize='15px'>
              Don't have an account?
            </Typography>
            <Link to='/signup' style={{ textDecoration: 'none' }}>
              <Typography
                variant='body2'
                sx={{
                  color: '#6366f1',
                  fontWeight: 600,
                  fontSize: '15px',
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: '#5558e3',
                  },
                }}
              >
                Sign Up
              </Typography>
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
